import React, { useState } from 'react';
import { X, Lock, AlertTriangle, Phone, Mail, User, ShoppingBag, Loader } from 'lucide-react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useCart } from './PreOrderCartContext';
import preOrderService from './PreOrderService';

const formatCurrency = (n) =>
  (Number.isFinite(n) ? n : 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// International phone validator using react-phone-number-input
const validatePhone = (phone) => {
  if (!phone) {
    return false;
  }
  
  return isValidPhoneNumber(phone);
};

const PreOrderCheckout = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState('form'); // 'form' or 'processing'
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    confirmEmail: '',
    phone: '',
    notes: '',
    futureUpdates: true, // Default to checked for better marketing
    agreeToTerms: false
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  
  const { cartItems, getTotalPrice, clearCart, setIsCartOpen } = useCart();

  const validateField = (name, value) => {
    switch(name) {
      case 'fullName':
        return value.trim().length < 2 ? 'Please enter your full name' : '';
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !emailRegex.test(value.trim()) ? 'Please enter a valid email' : '';
      case 'confirmEmail':
        return value.trim() !== formData.email.trim() ? 'Emails must match' : '';
      case 'phone':
        if (!value || !value.trim()) {
          return 'Phone number is required';
        }
        if (!validatePhone(value)) {
          return 'Please enter a valid phone number';
        }
        return '';
      default:
        return '';
    }
  };

  const handleFieldChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
    
    // Validate confirm email when email changes
    if (name === 'email' && touched.confirmEmail) {
      const confirmError = validateField('confirmEmail', formData.confirmEmail);
      setErrors(prev => ({ ...prev, confirmEmail: confirmError }));
    }
  };

  const handleBlur = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, formData[name]);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate all fields
    newErrors.fullName = validateField('fullName', formData.fullName);
    newErrors.email = validateField('email', formData.email);
    newErrors.confirmEmail = validateField('confirmEmail', formData.confirmEmail);
    newErrors.phone = validateField('phone', formData.phone);
    
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms';
    }
    
    if (cartItems.length === 0) {
      newErrors.cart = 'Your cart is empty';
    }
    
    // Remove empty errors
    Object.keys(newErrors).forEach(key => {
      if (!newErrors[key]) delete newErrors[key];
    });
    
    setErrors(newErrors);
    
    // Mark all fields as touched to show errors
    if (Object.keys(newErrors).length > 0) {
      setTouched({
        fullName: true,
        email: true,
        confirmEmail: true,
        phone: true
      });
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      // Scroll to first error
      const firstError = document.querySelector('.error-field');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    try {
      setLoading(true);
      setCurrentStep('processing');

      // Generate order ID
      const now = new Date();
      const orderId = `SFR-${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}-${now.getTime()}`;

      // Prepare order data for later use (will be sent via webhook)
      const orderData = {
        orderId,
        timestamp: now.toISOString(),
        fullName: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.replace(/[^\d+]/g, ''), // Keep + and digits only for storage
        items: cartItems.map(item => ({
          id: item.id,
          name: item.name,
          color: item.color,
          size: item.size,
          quantity: item.quantity,
          price: item.price
        })),
        totalAmount: getTotalPrice(),
        notes: formData.notes.trim(),
        futureUpdates: !!formData.futureUpdates,
      };

      // Create Stripe Checkout Session with order data in metadata
      const { url } = await preOrderService.createStripeCheckoutSession({
        items: orderData.items,
        orderId,
        email: orderData.email,
        customerData: {
          fullName: orderData.fullName,
          phone: orderData.phone,
          notes: orderData.notes,
          futureUpdates: orderData.futureUpdates,
          timestamp: orderData.timestamp,
          totalAmount: orderData.totalAmount
        }
      });

      // Clear cart and close modals before redirect
      clearCart();
      setIsCartOpen(false);

      // Small delay for better UX
      await new Promise(resolve => setTimeout(resolve, 500));

      // Redirect to Stripe Checkout
      window.location.href = url || '/';
    } catch (err) {
      console.error(err);
      setCurrentStep('form');
      setLoading(false);
      alert('Something went wrong starting checkout. Please try again.');
    }
  };

  // Processing screen
  if (currentStep === 'processing') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="fixed inset-0 bg-black/50" />
        <div className="relative bg-white rounded-lg p-12 text-center">
          <Loader className="animate-spin mx-auto mb-4" size={48} />
          <h2 className="text-2xl font-bold mb-2">Processing Your Order</h2>
          <p className="text-gray-600">Redirecting to secure payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 flex items-center justify-center">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        
        <div className="relative bg-white rounded-xl max-w-2xl w-full my-8 shadow-2xl">
          {/* Header */}
          <div className="p-6 border-b bg-gradient-to-r from-primary to-cta text-white rounded-t-xl">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <ShoppingBag size={28} />
                <h2 className="text-2xl font-bold">Complete Your Pre-Order</h2>
              </div>
              <button 
                onClick={onClose} 
                className="hover:bg-white/20 p-2 rounded-full transition-colors"
                aria-label="Close checkout"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Pre-order Notice */}
          <div className="bg-orange-50 p-4 border-b">
            <div className="flex gap-3">
              <AlertTriangle className="text-orange-600 flex-shrink-0 mt-0.5" size={20} />
              <div className="text-sm">
                <p className="font-bold text-orange-900 mb-1">Pre-Order Terms:</p>
                <ul className="space-y-1 text-orange-800">
                  <li>• Payment required now via Stripe (100% secure)</li>
                  <li>• Production time: 4–6 weeks</li>
                  <li>• Pickup at SFR events only (no shipping)</li>
                  <li>• All sales final (custom printed items)</li>
                </ul>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Order Summary */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-bold mb-3 text-gray-700">Order Summary</h3>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {cartItems.map((item, i) => (
                  <div key={`${item.id}-${item.size}-${i}`} className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {item.name} - {item.color} • Size {item.size} ×{item.quantity}
                    </span>
                    <span className="font-medium">{formatCurrency(item.price * item.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t mt-3 pt-3 flex justify-between font-bold text-lg">
                <span>Total:</span>
                <span className="text-primary">{formatCurrency(getTotalPrice())}</span>
              </div>
            </div>

            {errors.cart && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg font-medium">
                {errors.cart}
              </div>
            )}

            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="font-bold text-gray-700 flex items-center gap-2">
                <User size={20} />
                Contact Information
              </h3>

              {/* Full Name */}
              <div className={errors.fullName && touched.fullName ? 'error-field' : ''}>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Full Name *
                </label>
                <input
                  type="text"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors ${
                    errors.fullName && touched.fullName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  value={formData.fullName}
                  onChange={(e) => handleFieldChange('fullName', e.target.value)}
                  onBlur={() => handleBlur('fullName')}
                  placeholder="John Doe"
                  autoComplete="name"
                />
                {errors.fullName && touched.fullName && (
                  <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>
                )}
              </div>

              {/* Email fields in a grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={errors.email && touched.email ? 'error-field' : ''}>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    <Mail className="inline" size={16} /> Email *
                  </label>
                  <input
                    type="email"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors ${
                      errors.email && touched.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    value={formData.email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    onBlur={() => handleBlur('email')}
                    placeholder="john@example.com"
                    autoComplete="email"
                  />
                  {errors.email && touched.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                <div className={errors.confirmEmail && touched.confirmEmail ? 'error-field' : ''}>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Confirm Email *
                  </label>
                  <input
                    type="email"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors ${
                      errors.confirmEmail && touched.confirmEmail ? 'border-red-500' : 'border-gray-300'
                    }`}
                    value={formData.confirmEmail}
                    onChange={(e) => handleFieldChange('confirmEmail', e.target.value)}
                    onBlur={() => handleBlur('confirmEmail')}
                    placeholder="john@example.com"
                    autoComplete="email"
                  />
                  {errors.confirmEmail && touched.confirmEmail && (
                    <p className="text-red-500 text-sm mt-1">{errors.confirmEmail}</p>
                  )}
                </div>
              </div>

              {/* Phone */}
              <div className={errors.phone && touched.phone ? 'error-field' : ''}>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  <Phone className="inline" size={16} /> Phone Number *
                </label>
                <PhoneInput
                  international
                  defaultCountry="US"
                  countrySelectProps={{
                    "aria-label": "Select country",
                  }}
                  value={formData.phone}
                  onChange={(value) => {
                    handleFieldChange('phone', value);
                  }}
                  onBlur={() => handleBlur('phone')}
                  className={`w-full ${
                    errors.phone && touched.phone ? 'border-red-500' : ''
                  }`}
                  style={{
                    '--PhoneInputCountrySelectArrow-color': '#6B7280',
                    '--PhoneInputCountrySelect-marginRight': '0.75rem',
                  }}
                />
                {errors.phone && touched.phone && (
                  <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Select your country and enter your phone number
                </p>
              </div>

              {/* Notes */}
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Special Requests (Optional)
                </label>
                <textarea
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                  rows="2"
                  value={formData.notes}
                  onChange={(e) => handleFieldChange('notes', e.target.value)}
                  placeholder="Any special requests or preferred pickup location?"
                />
              </div>
            </div>

            {/* Checkboxes */}
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
                <input
                  type="checkbox"
                  checked={formData.futureUpdates}
                  onChange={(e) => setFormData({...formData, futureUpdates: e.target.checked})}
                  className="mt-0.5 w-4 h-4 text-primary focus:ring-primary/50"
                />
                <span className="text-sm text-gray-700">
                  Send me updates about new merch and SFR events (recommended)
                </span>
              </label>

              <label className={`flex items-start gap-3 cursor-pointer p-2 rounded transition-colors ${
                errors.agreeToTerms ? 'bg-red-50' : 'hover:bg-gray-50'
              }`}>
                <input
                  type="checkbox"
                  checked={formData.agreeToTerms}
                  onChange={(e) => setFormData({...formData, agreeToTerms: e.target.checked})}
                  className="mt-0.5 w-4 h-4 text-primary focus:ring-primary/50"
                />
                <span className="text-sm text-gray-700">
                  I understand this is a pre-order with prepayment required. Items will be ready in 4-6 weeks
                  and I'll pick them up at an SFR event. No international shipping. All sales are final. *
                </span>
              </label>
              {errors.agreeToTerms && (
                <p className="text-red-500 text-sm ml-7">{errors.agreeToTerms}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-primary to-cta text-white py-4 rounded-lg font-bold 
                hover:shadow-lg transform hover:scale-[1.02] transition-all flex items-center justify-center gap-3
                disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <Lock size={20} />
              {loading ? 'Processing...' : `Pay ${formatCurrency(getTotalPrice())} with Stripe`}
            </button>

            <p className="text-xs text-center text-gray-500">
              Your payment info is secure and never stored on our servers.
              You'll be redirected to Stripe's secure checkout.
            </p>
          </form>
        </div>
      </div>
      
      {/* Custom phone input styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .PhoneInputCountry {
            margin-right: 0.75rem;
          }
          .PhoneInputCountrySelect {
            padding: 0.5rem;
            border-radius: 0.5rem;
            border: 1px solid #d1d5db;
            background-color: white;
          }
          .PhoneInputCountrySelect:focus {
            border-color: #53C2C8;
            outline: none;
            box-shadow: 0 0 0 2px rgba(83, 194, 200, 0.2);
          }
          .PhoneInputInput {
            font-family: inherit;
            padding: 0.5rem 1rem;
            border-radius: 0.5rem;
            border: 1px solid #d1d5db;
            background-color: white;
            transition: all 0.2s;
          }
          .PhoneInputInput:focus {
            border-color: #53C2C8;
            outline: none;
            box-shadow: 0 0 0 2px rgba(83, 194, 200, 0.2);
          }
          .error-field .PhoneInputCountrySelect,
          .error-field .PhoneInputInput {
            border-color: #ef4444;
          }
          .error-field .PhoneInputCountrySelect:focus,
          .error-field .PhoneInputInput:focus {
            border-color: #ef4444;
            box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
          }
        `
      }} />
    </div>
  );
};

export default PreOrderCheckout;